/**
 * Pure helpers for project / card / epic identity on GitHub and Jira cards.
 */

import type { AttentionItem, JiraIssue } from './types';

export const JIRA_PROJECT_CHIP_COLLAPSED_MAX = 3;

/** Short repo name: `owner/repo` → `repo`; bare names pass through. */
export function shortRepoName(repo: string | undefined | null): string {
  const trimmed = (repo ?? '').trim();
  if (!trimmed) return '';
  const slash = trimmed.lastIndexOf('/');
  if (slash < 0 || slash === trimmed.length - 1) return trimmed;
  return trimmed.slice(slash + 1);
}

/** Collapsed GitHub identity: `carrier-web · #412`. */
export function formatGitHubProjectPrLabel(
  repo: string | undefined | null,
  prNumber: number | string | undefined | null,
): string {
  const project = shortRepoName(repo);
  const raw = prNumber == null ? '' : String(prNumber).trim();
  const num = raw.replace(/^#/, '');
  if (!project && !num) return '';
  if (!project) return `#${num}`;
  if (!num) return project;
  return `${project} · #${num}`;
}

/** Jira linked-PR chip label: `carrier-web · PR #412`. */
export function formatJiraLinkedPrChipLabel(
  repo: string | undefined | null,
  prNumber: number | string | undefined | null,
): string {
  const project = shortRepoName(repo);
  const raw = prNumber == null ? '' : String(prNumber).trim();
  const num = raw.replace(/^#/, '');
  const prPart = num ? `PR #${num}` : 'PR';
  if (!project) return prPart;
  return `${project} · ${prPart}`;
}

export type JiraProjectChip = {
  name: string;
  active: boolean;
  /** GitHub attention id when a linked PR uniquely (or most recently) matches. */
  githubItemId?: string;
};

function repoFromGitHubItem(item: AttentionItem): string {
  return (
    item.rawPullRequest?.head.repo ||
    item.rawPullRequest?.base.repo ||
    item.secondary?.split(' · ')[0] ||
    ''
  );
}

function prNumberFromGitHubItem(item: AttentionItem): string {
  if (item.rawPullRequest?.number != null) return String(item.rawPullRequest.number);
  return (item.primary || '').replace(/^#/, '');
}

/** Prefer most recently updated PR when several share a repo. */
export function pickGitHubItemForProject(
  linkedPrs: readonly AttentionItem[],
  projectName: string,
): AttentionItem | undefined {
  const target = projectName.trim().toLowerCase();
  if (!target) return undefined;
  const matches = linkedPrs.filter((pr) => shortRepoName(repoFromGitHubItem(pr)).toLowerCase() === target);
  if (matches.length === 0) return undefined;
  return [...matches].sort((a, b) => {
    const ta = a.updatedAt ? Date.parse(a.updatedAt) : 0;
    const tb = b.updatedAt ? Date.parse(b.updatedAt) : 0;
    return tb - ta;
  })[0];
}

/**
 * Projects a Jira card touches: active (linked PRs) first, then declared components.
 * De-duped, first-seen order preserved.
 */
export function projectsForJiraCard(
  issue: Pick<JiraIssue['fields'], 'components'> | undefined,
  linkedPrs: readonly AttentionItem[],
): JiraProjectChip[] {
  const byName = new Map<string, JiraProjectChip>();

  for (const pr of linkedPrs) {
    const name = shortRepoName(repoFromGitHubItem(pr));
    if (!name) continue;
    const key = name.toLowerCase();
    const existing = byName.get(key);
    if (existing) {
      existing.active = true;
      continue;
    }
    byName.set(key, { name, active: true, githubItemId: pr.id });
  }

  for (const component of issue?.components ?? []) {
    const name = component.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (byName.has(key)) continue;
    byName.set(key, { name, active: false });
  }

  // Refresh githubItemId to most recent PR per active project
  for (const chip of byName.values()) {
    if (!chip.active) continue;
    const picked = pickGitHubItemForProject(linkedPrs, chip.name);
    if (picked) chip.githubItemId = picked.id;
  }

  return [...byName.values()];
}

export function collapseProjectChips<T>(
  chips: readonly T[],
  maxVisible: number = JIRA_PROJECT_CHIP_COLLAPSED_MAX,
): { visible: T[]; overflow: number } {
  if (chips.length <= maxVisible) return { visible: [...chips], overflow: 0 };
  return {
    visible: chips.slice(0, maxVisible),
    overflow: chips.length - maxVisible,
  };
}

export function githubIdentityFromAttentionItem(item: AttentionItem): {
  projectPrLabel: string;
  linkedTicketKey?: string;
} {
  const repo = repoFromGitHubItem(item);
  const number = prNumberFromGitHubItem(item);
  return {
    projectPrLabel: formatGitHubProjectPrLabel(repo, number),
    linkedTicketKey: item.ticket?.trim() || undefined,
  };
}

export function linkedPrChipLabelFromAttentionItem(pr: AttentionItem): string {
  return formatJiraLinkedPrChipLabel(repoFromGitHubItem(pr), prNumberFromGitHubItem(pr));
}

/** Keys used for inspector sibling discovery (ticket, refs, issue links, epic). */
export function attentionSiblingKeys(item: AttentionItem): string[] {
  const linkKeys = item.rawJiraIssue?.fields.issueLinks?.map((l) => l.linkedIssueKey) ?? [];
  const epic = item.parentEpicKey || item.rawJiraIssue?.fields.parentEpicKey;
  return [item.ticket, ...(item.refs || []).map((r) => r.key), ...linkKeys, epic]
    .map((k) => (typeof k === 'string' ? k.trim() : ''))
    .filter(Boolean);
}

/**
 * Precomputes {@link attentionSiblingKeys} for every item, keyed by `item.id`.
 * Callers must look up by the exact object currently held in `items` (e.g. via
 * `getItemById`) — a different object sharing the same `id` would silently
 * read this object's sibling keys instead of its own.
 */
export function buildSiblingKeysMap(items: AttentionItem[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const item of items) {
    map.set(item.id, attentionSiblingKeys(item));
  }
  return map;
}
